import { baseApi } from "@/redux/api/base-api";
import type { RootState } from "@/redux/store";
import {
  hydrateNotifications,
  markAllNotificationsReadLocally,
  markNotificationReadLocally,
  selectNotificationsState,
} from "../state/notifications-slice";
import type {
  GetNotificationsParams,
  GetNotificationsResponse,
  GetUnreadCountResponse,
  MarkAllNotificationsReadResponse,
  MarkNotificationReadResponse,
  NotificationItem,
  NotificationReadSyncEvent,
  NotificationUnreadCountEvent,
  NotificationsListState,
} from "../types/notification.types";

const NOTIFICATIONS_ENDPOINT = "/b2c/profile/notifications";
const NOTIFICATION_LIST_TAG = {
  type: "NotificationList" as const,
  id: "LIST",
};
const NOTIFICATION_UNREAD_TAG = {
  type: "NotificationUnreadCount" as const,
  id: "COUNT",
};

function toRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

function toString(value: unknown) {
  return typeof value === "string" ? value : null;
}

function toNumber(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function toBoolean(value: unknown) {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value === 1;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "1" || normalized === "true";
  }

  return false;
}

function toNotificationItem(value: unknown): NotificationItem | null {
  const raw = toRecord(value);
  if (!raw) {
    return null;
  }

  const id = toNumber(raw.id);
  const title = toString(raw.title);
  const message = toString(raw.message);
  const eventType = toString(raw.event_type);
  const service = toString(raw.service);
  const entityType = toString(raw.entity_type);
  const entityId = toNumber(raw.entity_id);
  const createdAt = toString(raw.created_at);

  if (
    id === null ||
    title === null ||
    message === null ||
    eventType === null ||
    service === null ||
    entityType === null ||
    entityId === null ||
    createdAt === null
  ) {
    return null;
  }

  return {
    id,
    title,
    message,
    event_type: eventType,
    service,
    entity_type: entityType as NotificationItem["entity_type"],
    entity_id: entityId,
    payload: toRecord(raw.payload),
    is_read: toBoolean(raw.is_read),
    created_at: createdAt,
  };
}

function extractNotificationItems(payload: unknown): NotificationItem[] {
  const topLevel = toRecord(payload);
  const nestedData = toRecord(topLevel?.data);
  const candidates = [
    topLevel?.data,
    nestedData?.notifications,
    nestedData?.items,
    nestedData?.rows,
    topLevel?.notifications,
    topLevel?.items,
    topLevel?.rows,
  ];

  for (const candidate of candidates) {
    if (!Array.isArray(candidate)) {
      continue;
    }

    return candidate
      .map((item) => toNotificationItem(item))
      .filter((item): item is NotificationItem => item !== null);
  }

  return [];
}

function extractNotificationsResponse(
  response: GetNotificationsResponse | Record<string, unknown>,
  params?: GetNotificationsParams | void,
): NotificationsListState {
  const topLevel = toRecord(response);
  const nestedData = toRecord(topLevel?.data);
  const items = extractNotificationItems(response);
  const resolvedTotal =
    toNumber(topLevel?.total) ??
    toNumber(nestedData?.total) ??
    toNumber(nestedData?.count);
  const total = resolvedTotal ?? items.length;
  const skip = params?.skip ?? 0;
  const limit = params?.limit;
  const hasMore =
    typeof resolvedTotal === "number"
      ? skip + items.length < resolvedTotal
      : typeof limit === "number"
        ? items.length >= limit
        : false;
  const unreadCount =
    toNumber(topLevel?.unread_count) ??
    toNumber(nestedData?.unread_count) ??
    toNumber(topLevel?.unreadCount) ??
    toNumber(nestedData?.unreadCount);

  return {
    items,
    total,
    hasMore,
    unreadCount: unreadCount ?? undefined,
  };
}

function extractUnreadCountResponse(
  response: GetUnreadCountResponse | Record<string, unknown>,
) {
  return toNumber(toRecord(response.data)?.unread_count) ?? 0;
}

function createSnapshot(state: ReturnType<typeof selectNotificationsState>) {
  return {
    items: state.items.map((item) => ({ ...item })),
    total: state.total,
    hasMore: state.hasMore,
    unreadCount: state.unreadCount,
  };
}

function extractUnreadCountEvent(payload: unknown): number | null {
  const raw = toRecord(payload) as NotificationUnreadCountEvent | null;
  return raw ? toNumber(raw.count) : null;
}

function extractReadSyncEvent(payload: unknown): NotificationReadSyncEvent | null {
  const raw = toRecord(payload);
  if (!raw) {
    return null;
  }

  if (raw.read_all === true) {
    return { read_all: true };
  }

  const id = toNumber(raw.id);
  if (id !== null && raw.read === true) {
    return { id, read: true };
  }

  return null;
}

export const notificationsApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getNotifications: builder.query<
      NotificationsListState,
      GetNotificationsParams | void
    >({
      query: (params) => ({
        url: NOTIFICATIONS_ENDPOINT,
        method: "GET",
        params: params ?? undefined,
      }),
      transformResponse: (
        response: GetNotificationsResponse | Record<string, unknown>,
        _meta,
        params,
      ) => extractNotificationsResponse(response, params),
      providesTags: (result) => {
        const itemTags =
          result?.items.map((item) => ({
            type: "Notification" as const,
            id: item.id,
          })) ?? [];

        return [...itemTags, NOTIFICATION_LIST_TAG, NOTIFICATION_UNREAD_TAG];
      },
    }),
    getUnreadNotificationCount: builder.query<number, void>({
      query: () => ({
        url: `${NOTIFICATIONS_ENDPOINT}/unread-count`,
        method: "GET",
      }),
      transformResponse: (
        response: GetUnreadCountResponse | Record<string, unknown>,
      ) => extractUnreadCountResponse(response),
      providesTags: [NOTIFICATION_UNREAD_TAG],
    }),
    markNotificationRead: builder.mutation<MarkNotificationReadResponse, number>({
      query: (id) => ({
        url: `${NOTIFICATIONS_ENDPOINT}/${id}/read`,
        method: "PATCH",
      }),
      async onQueryStarted(id, { dispatch, getState, queryFulfilled }) {
        const previousState = createSnapshot(
          selectNotificationsState(getState() as RootState),
        );

        dispatch(markNotificationReadLocally(id));

        try {
          await queryFulfilled;
        } catch {
          dispatch(hydrateNotifications(previousState));
        }
      }
    }),
    markAllNotificationsRead: builder.mutation<
      MarkAllNotificationsReadResponse,
      void
    >({
      query: () => ({
        url: `${NOTIFICATIONS_ENDPOINT}/read-all`,
        method: "PATCH",
      }),
      async onQueryStarted(_, { dispatch, getState, queryFulfilled }) {
        const previousState = createSnapshot(
          selectNotificationsState(getState() as RootState),
        );

        dispatch(markAllNotificationsReadLocally());

        try {
          await queryFulfilled;
        } catch {
          dispatch(hydrateNotifications(previousState));
        }
      }
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useLazyGetNotificationsQuery,
  useGetUnreadNotificationCountQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
} = notificationsApi;

export const notificationsApiHelpers = {
  toNotificationItem,
  extractUnreadCountEvent,
  extractReadSyncEvent,
};
